import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Chip, Drawer } from '@/components/ui';
import type { AppRoleOption } from '@/api/console';
import styles from './CatalogDrawer.module.css';

interface CatalogEntry {
    option: AppRoleOption;
    /** Standard roles already linked to this app role, by display name. */
    linkedTo: string[];
    /** The app stopped reporting it — every link pointing here is paused. */
    lost: boolean;
}

/**
 * EAA-1817 (design frame 55e): "Xem danh mục" as a 440px drawer listing the
 * application's role catalog, lost roles first, with an inline "Liên kết" on
 * anything unused.
 *
 * WHY A DRAWER AND NOT THE INLINE LIST IT REPLACES. The old viewer was a `<ul>`
 * that expanded INSIDE the editor card, pushing the role table down — so reading
 * the catalog and reading the links it feeds became mutually exclusive, which is
 * the one comparison this list exists to support. A drawer sits beside the table.
 *
 * ORDER IS THE POINT. Lost roles go to the top on a tinted ground: those are the
 * rows where something is already broken, and burying them alphabetically among
 * sixteen healthy ones is how they stay broken. Then unused roles (an
 * opportunity), then linked ones (nothing to do).
 *
 * The inline "Liên kết" opens the picker on the role table rather than writing
 * anything: a link needs a STANDARD ROLE to attach to, and the catalog knows only
 * the app side of the pair. Offering a one-click "link" here would have to guess
 * the other half.
 */
export function CatalogDrawer({
    open,
    appName,
    options,
    loading,
    linkedByAppRole,
    lostAppRoles,
    onClose,
    onLink,
}: {
    open: boolean;
    appName: string;
    options: AppRoleOption[];
    loading: boolean;
    /** app-role key → standard-role display names already linked to it. */
    linkedByAppRole: Map<string, string[]>;
    /** app-role keys the app no longer reports. */
    lostAppRoles: Set<string>;
    onClose: () => void;
    /** Omitted for a caller without manage rights — the row then just reads. */
    onLink?: (appRoleKey: string) => void;
}) {
    const { t } = useTranslation();

    const entries = useMemo<CatalogEntry[]>(() => {
        const rows: CatalogEntry[] = options.map((option) => ({
            option,
            linkedTo: linkedByAppRole.get(option.key) ?? [],
            lost: lostAppRoles.has(option.key),
        }));
        // Lost first, then unused, then linked. Stable within each band on the
        // server's own order so the list does not reshuffle between opens.
        const rank = (entry: CatalogEntry) =>
            entry.lost ? 0 : entry.linkedTo.length === 0 ? 1 : 2;
        return rows.sort((a, b) => rank(a) - rank(b));
    }, [options, linkedByAppRole, lostAppRoles]);

    const lostCount = entries.filter((entry) => entry.lost).length;

    return (
        <Drawer
            open={open}
            onClose={onClose}
            width={440}
            title={t('console.roleCoverage.catalogDrawer.title', { appName })}
        >
            <p className="portal-admin-hint">
                {t('console.roleCoverage.catalogDrawer.subtitle', {
                    count: options.length,
                    appName,
                })}
                {lostCount > 0 &&
                    ` · ${t('console.roleCoverage.catalogDrawer.lostCount', { count: lostCount })}`}
            </p>

            {loading && (
                <p className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.loadingCatalogViewer')}
                </p>
            )}

            {!loading && entries.length === 0 && (
                <p className="portal-admin-hint">
                    {t('console.roleCoverage.editorCard.catalogEmpty')}
                </p>
            )}

            <ul className={styles.drawer}>
                {entries.map(({ option, linkedTo, lost }) => (
                    <li key={option.id} className={styles.row + (lost ? ` ${styles.rowLost}` : '')}>
                        <span className={styles.identity}>
                            <span className={styles.name}>{option.name || option.key}</span>
                            <code className={styles.key}>{option.key}</code>
                        </span>

                        <span className={styles.meta}>
                            {lost && (
                                <Chip tone="stale" mono={false} icon={false}>
                                    {t('console.roleCoverage.catalogDrawer.lost')}
                                </Chip>
                            )}
                            {!lost && linkedTo.length > 0 && (
                                <span className="portal-admin-hint">
                                    {t('console.roleCoverage.catalogDrawer.linkedTo', {
                                        roles: linkedTo.join(', '),
                                    })}
                                </span>
                            )}
                            {!lost && linkedTo.length === 0 && onLink && (
                                <Button size="small" onClick={() => onLink(option.key)}>
                                    {t('console.roleCoverage.catalogDrawer.link')}
                                </Button>
                            )}
                            {!lost && linkedTo.length === 0 && !onLink && (
                                <span className="portal-admin-hint">
                                    {t('console.roleCoverage.catalogDrawer.unused')}
                                </span>
                            )}
                        </span>
                    </li>
                ))}
            </ul>
        </Drawer>
    );
}

export default CatalogDrawer;
